import React, { useState, useEffect, useRef } from 'react';
import { PageHeader, Btn, Badge, Modal } from '../../components/UI';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const PRI_COLORS = { low: '#666', medium: '#ffd700', high: '#ff6b6b' };

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [newModal, setNewModal] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'medium' });
  const [creating, setCreating] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const bottomRef = useRef();

  const loadTickets = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/tickets');
      if (data.success) setTickets(data.tickets);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadTickets(); }, []);

  // Auto scroll to bottom when replies update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  const openTicket = async (ticket) => {
    setActiveTicket(ticket);
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/api/tickets/${ticket.id}`);
      if (data.success) {
        setReplies(data.replies);
        // Update unread count locally
        setTickets(ts => ts.map(t => t.id === ticket.id ? { ...t, unread_count: 0 } : t));
      }
    } finally { setLoadingThread(false); }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/api/tickets/${activeTicket.id}/reply`, { message: replyText.trim() });
      if (data.success) {
        setReplies(prev => [...prev, { sender: 'client', message: replyText.trim(), created_at: new Date().toISOString() }]);
        setReplyText('');
        setTickets(ts => ts.map(t => t.id === activeTicket.id ? { ...t, status: 'open', updated_at: new Date().toISOString() } : t));
      } else toast.error(data.message);
    } finally { setSending(false); }
  };

  const closeTicket = async () => {
    if (!window.confirm('Close this ticket?')) return;
    const { data } = await api.put(`/api/tickets/${activeTicket.id}/close`, {});
    if (data.success) {
      toast.success('Ticket closed');
      setActiveTicket(t => ({ ...t, status: 'closed' }));
      setTickets(ts => ts.map(t => t.id === activeTicket.id ? { ...t, status: 'closed' } : t));
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) { toast.error('Fill all fields'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/api/tickets', form);
      if (data.success) {
        toast.success(`Ticket #${data.ticket_number} created!`);
        setNewModal(false);
        setForm({ subject: '', message: '', priority: 'medium' });
        await loadTickets();
        // Open the new ticket
        const { data: td } = await api.get(`/api/tickets/${data.id}`);
        if (td.success) { setActiveTicket(td.ticket); setReplies(td.replies); }
      } else toast.error(data.message);
    } finally { setCreating(false); }
  };

  const formatTime = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  return (
    <div>
      <PageHeader title="Support Tickets" action={
        <Btn onClick={() => setNewModal(true)}>➕ New Ticket</Btn>
      } />

      {/* Contact bar */}
      <div style={{ background: 'rgba(0,231,76,0.04)', border: '1px solid rgba(0,231,76,0.12)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <span>⚡ Avg response time: <strong style={{ color: '#fff' }}>under 4 hours</strong></span>
        <span>•</span>
        <span>📧 <a href="mailto:halo@msdiscoveries.com" style={{ color: 'var(--green)' }}>halo@msdiscoveries.com</a></span>
        <span>•</span>
        <span>📞 <a href="tel:+919443724724" style={{ color: 'var(--green)' }}>+91 9443724724</a></span>
        <span>•</span>
        <span>🕐 Mon–Sat 10AM–6PM IST</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, height: 'calc(100vh - 260px)', minHeight: 500 }}>

        {/* ── LEFT: Ticket List ── */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.5)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>All Tickets</span>
            <span style={{ background: 'var(--green-dim)', color: 'var(--green)', padding: '2px 8px', borderRadius: 100, fontSize: 11 }}>{tickets.length}</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
            ) : tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🎫</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>No tickets yet</div>
                <Btn size="sm" onClick={() => setNewModal(true)} style={{ marginTop: 16 }}>Create First Ticket</Btn>
              </div>
            ) : tickets.map(t => (
              <div key={t.id} onClick={() => openTicket(t)} style={{
                padding: '14px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                background: activeTicket?.id === t.id ? 'rgba(0,231,76,0.06)' : 'transparent',
                borderLeft: activeTicket?.id === t.id ? '3px solid var(--green)' : '3px solid transparent',
                transition: 'all 0.15s'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#fff', flex: 1, marginRight: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject}</div>
                  {t.unread_count > 0 && (
                    <span style={{ background: 'var(--green)', color: '#000', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{t.unread_count}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge>{t.status}</Badge>
                  <span style={{ background: `${PRI_COLORS[t.priority]}22`, color: PRI_COLORS[t.priority], padding: '2px 7px', borderRadius: 100, fontSize: 10, fontWeight: 700 }}>{t.priority?.toUpperCase()}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginLeft: 'auto' }}>{formatTime(t.updated_at || t.created_at)}</span>
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>#{t.ticket_number}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT: Chat Thread ── */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!activeTicket ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>💬</div>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Select a ticket</div>
              <div style={{ fontSize: 13 }}>Choose a ticket from the left to view the conversation</div>
              <Btn onClick={() => setNewModal(true)} style={{ marginTop: 24 }}>➕ Create New Ticket</Btn>
            </div>
          ) : (
            <>
              {/* Thread Header */}
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.02)' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>{activeTicket.subject}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>#{activeTicket.ticket_number}</span>
                    <Badge>{activeTicket.status}</Badge>
                    <span style={{ background: `${PRI_COLORS[activeTicket.priority]}22`, color: PRI_COLORS[activeTicket.priority], padding: '2px 7px', borderRadius: 100, fontSize: 10, fontWeight: 700 }}>{activeTicket.priority?.toUpperCase()}</span>
                  </div>
                </div>
                {activeTicket.status !== 'closed' && (
                  <Btn variant="danger" size="sm" onClick={closeTicket}>✕ Close Ticket</Btn>
                )}
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {loadingThread ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)' }}>Loading messages...</div>
                ) : replies.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No messages yet</div>
                ) : replies.map((r, i) => {
                  const isClient = r.sender === 'client';
                  return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isClient ? 'flex-end' : 'flex-start' }}>
                      {/* Sender label */}
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 4, paddingLeft: isClient ? 0 : 12, paddingRight: isClient ? 12 : 0 }}>
                        {isClient ? 'You' : '🛡 MS Reach Support'} · {formatTime(r.created_at)}
                      </div>
                      {/* Bubble */}
                      <div style={{
                        maxWidth: '72%', padding: '12px 16px', borderRadius: isClient ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                        background: isClient ? 'var(--green)' : 'rgba(255,255,255,0.07)',
                        color: isClient ? '#000' : '#fff',
                        fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        boxShadow: isClient ? '0 2px 12px rgba(0,231,76,0.2)' : 'none'
                      }}>
                        {r.message}
                      </div>
                    </div>
                  );
                })}

                {/* Closed notice */}
                {activeTicket.status === 'closed' && (
                  <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(255,255,255,0.04)', borderRadius: 8, fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>
                    🔒 This ticket is closed. Create a new ticket if you need further help.
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Reply Input */}
              {activeTicket.status !== 'closed' && (
                <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'flex-end', background: 'rgba(255,255,255,0.01)' }}>
                  <textarea
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                    style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 16px', color: '#fff', fontSize: 14, resize: 'none', minHeight: 48, maxHeight: 120, lineHeight: 1.5, outline: 'none', fontFamily: 'DM Sans' }}
                  />
                  <button onClick={sendReply} disabled={sending || !replyText.trim()} style={{ background: replyText.trim() ? 'var(--green)' : 'rgba(255,255,255,0.06)', color: replyText.trim() ? '#000' : 'rgba(255,255,255,0.2)', border: 'none', width: 48, height: 48, borderRadius: 12, cursor: replyText.trim() ? 'pointer' : 'default', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                    {sending ? '⏳' : '➤'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── New Ticket Modal ── */}
      <Modal open={newModal} onClose={() => setNewModal(false)} title="🎫 New Support Ticket" width={500}>
        <form onSubmit={createTicket}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 6, fontWeight: 500 }}>Subject *</label>
            <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Brief description of your issue" required style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: '#fff', fontSize: 14 }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 6, fontWeight: 500 }}>Priority</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['low','🟢 Low'],['medium','🟡 Medium'],['high','🔴 High']].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setForm({ ...form, priority: val })} style={{ flex: 1, padding: '8px', borderRadius: 8, border: `1px solid ${form.priority === val ? PRI_COLORS[val] : 'rgba(255,255,255,0.1)'}`, background: form.priority === val ? `${PRI_COLORS[val]}18` : 'transparent', color: form.priority === val ? PRI_COLORS[val] : 'rgba(255,255,255,0.4)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}>{label}</button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 6, fontWeight: 500 }}>Message *</label>
            <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Describe your issue in detail. The more detail you provide, the faster we can help..." required style={{ width: '100%', height: 140, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '12px 14px', color: '#fff', resize: 'none', fontSize: 13, lineHeight: 1.6 }} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Btn type="submit" disabled={creating} full>{creating ? 'Creating...' : 'Create Ticket'}</Btn>
            <Btn variant="outline" onClick={() => setNewModal(false)} full>Cancel</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}